import { ReactElement, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { DropoffAction, ORDER_METHODS, PaymentMethod, REDUX_STORE_SLICES } from '../../services/exports/Constants';
import useTheme from '../../hooks/ui/useTheme';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { SectionType } from './sections/CheckoutInfoBag';
import useAuth from '../../hooks/global/useAuth';
import collect from 'collect.js';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import usePaymentFlow from '../../hooks/payment/usePaymentFlow';
import PaymentDetails from '../payment/payment-methods/PaymentDetails';
import { Checkbox, Radio } from '@mui/material';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import { Modals } from '../../modules/mobile/exports/Enums';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import useStore from '../../hooks/store/useStore';
import HelperMethods from '../../services/exports/HelperMethods';
import moment from 'moment';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import { OrderMethodModalProps } from '../../services/exports/Interfaces';
import { ORDER_TIME_OPTIONS, OrderTimeOption } from '../../modals/order-method/useOrderTimeOptions';
import Theme from '../Theme';

interface Props {
  toggleSection: (section: SectionType, open: boolean) => any;
}

interface SectionProps {
  title: string;
  children: any;
  onChange?: () => any;
  hideBorder?: boolean;
}

export default function OrderInfo(props: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:OrderInfo' });

  const { toggleSection } = props;

  const theme = useTheme();
  const { isGuest } = useAuth();
  const { toggleOrderMethodModal } = useAppMethods();
  const { selectedPaymentMethod, paymentOptions, findSavedPaymentMethod } = usePaymentFlow();
  const { data, localData, update } = useOrderFlow();

  const savedPaymentMethod = useMemo(
    () => findSavedPaymentMethod(selectedPaymentMethod),
    [selectedPaymentMethod, paymentOptions],
  );

  const orderDetailsInfo = useMemo(
    () =>
      collect()
        .when(localData.method === ORDER_METHODS.DELIVERY, (collection) =>
          collection
            .merge([localData.delivery_doorbell_name, localData.delivery_company_name, localData.customer_note])
            .whereNotNull(),
        )
        .when(localData.method === ORDER_METHODS.ROOM_SERVICE, (collection) =>
          collection.merge([localData.order_instructions]).whereNotNull(),
        )
        .implode(', '),
    [
      localData.method,
      localData.delivery_doorbell_name,
      localData.delivery_company_name,
      localData.customer_note,
      localData.order_instructions,
    ],
  );

  const fullName = collect([localData.customer_first_name, localData.customer_last_name])
    .filter((item) => Boolean(item))
    .implode(' ');

  const invoiceInfo = collect([
    localData.invoice_company_name,
    localData.invoice_tax_id,
    localData.invoice_address,
    localData.invoice_email,
  ])
    .filter((item) => Boolean(item))
    .implode(', ');

  useEffect(() => {
    !localData.delivery_dropoff_action && update('delivery_dropoff_action', DropoffAction.RingDoorBell);
  }, []);

  useEffect(() => {
    localData.is_receipt_required && !invoiceInfo && toggleSection(SectionType.invoice_info, true);
  }, [localData.is_receipt_required]);

  const openOrderMethodInfoSection = () => toggleSection(SectionType.order_method_details, true);
  const openCustomerInfoSection = () => toggleSection(SectionType.customer_info, true);
  const openPaymentInfoSection = () => toggleSection(SectionType.payment_info, true);
  const openInvoiceInfoSection = () => toggleSection(SectionType.invoice_info, true);

  const { isMobileApp } = useMobileApp();
  const { openModal } = useModalRouter();

  const onChangeOrderMethod = (props?: Partial<OrderMethodModalProps>): void => {
    if (isMobileApp) {
      return openModal({
        id: Modals.OrderMethod,
        props,
      });
    }

    toggleOrderMethodModal({ openModal: true, ...props });
  };

  return (
    <div className="space-y-md">
      <OrderInfo.Method onChange={onChangeOrderMethod} />
      <OrderInfo.Time onChange={onChangeOrderMethod} />
      <OrderInfo.Location onChange={onChangeOrderMethod} />

      {localData.method === ORDER_METHODS.DELIVERY && (
        <OrderInfo.Row
          title={t('sections.delivery_info.title')}
          onChange={orderDetailsInfo ? openOrderMethodInfoSection : null}
        >
          {Object.values(DropoffAction).map((item) => (
            <button
              className="flex flex-row w-fit items-center mt-xs"
              onClick={() => update('delivery_dropoff_action', item)}
              key={`dropoff-action-${item}`}
            >
              <Radio
                checked={localData.delivery_dropoff_action === item}
                sx={{
                  color: theme.color.black.DEFAULT,
                  '&.Mui-checked': {
                    color: theme.color.black.DEFAULT,
                  },
                }}
                key={`delivery-dropoff-action-${item}`}
              />
              <p className="ml-xs my-auto text-sm">{t(`form.dropoff_action.options.${item}`)}</p>
            </button>
          ))}
          {(isGuest || !orderDetailsInfo) && (
            <button className="underline text-sm mt-xs" onClick={openOrderMethodInfoSection}>
              {t('buttons.add_delivery_note')}
            </button>
          )}
          {Boolean(orderDetailsInfo) && <p className="text-sm mt-xs">{orderDetailsInfo}</p>}
        </OrderInfo.Row>
      )}
      {!isGuest && (
        <OrderInfo.Row title={t('sections.customer_info.title')} onChange={openCustomerInfoSection}>
          {fullName && <p className="text-sm">{fullName}</p>}
          {Boolean(localData.customer_email) && <p className="text-sm">{localData.customer_email}</p>}
          {Boolean(localData.customer_phone_number) && (
            <p className="text-sm">{formatPhoneNumberIntl(localData.customer_phone_number)}</p>
          )}
        </OrderInfo.Row>
      )}
      {data.total > 0 && selectedPaymentMethod && (
        <OrderInfo.Row title={t('sections.payment.title')} onChange={openPaymentInfoSection}>
          <PaymentDetails
            {...savedPaymentMethod}
            payment_method_id={selectedPaymentMethod}
            hideIcons={selectedPaymentMethod === PaymentMethod.Card && !savedPaymentMethod}
            classes={{
              icon: 'h-5',
            }}
          />
        </OrderInfo.Row>
      )}
      {data.total > 0 && !isGuest && (
        <OrderInfo.Row title={t('sections.request_invoice.title')} onChange={openInvoiceInfoSection}>
          <div className="flex">
            <Checkbox
              checked={localData.is_receipt_required}
              onChange={(e) => update('is_receipt_required', e.target.checked)}
              id="is-receipt-required-duplicate"
              sx={{
                color: Theme.color.blue['300'],
                '&.Mui-checked': {
                  color: Theme.color.blue['300'],
                },
              }}
            />
            <label htmlFor="is-receipt-required-duplicate" className="text-sm ml-xs my-auto">
              {t('sections.request_invoice.form.is_receipt_required.label')}
            </label>
          </div>
          {localData.is_receipt_required && Boolean(invoiceInfo) && (
            <p className="!text-xs mt-xs !font-medium text-brand-text-secondary">{invoiceInfo}</p>
          )}
        </OrderInfo.Row>
      )}
    </div>
  );
}

OrderInfo.Method = ({ onChange }: { onChange: (props?: Partial<OrderMethodModalProps>) => void }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:OrderInfo' });
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { cached_order } = useStore<REDUX_STORE_SLICES.ORDER>(REDUX_STORE_SLICES.ORDER);
  const { visibleOrderMethods } = useOrderMethodsStatus(company);

  return (
    <OrderInfo.Row title={t('sections.method.title')}>
      {visibleOrderMethods.map((orderMethod) => (
        <button
          className="flex flex-row w-fit items-center mt-xs"
          onClick={() => onChange({ openModal: true, orderMethod })}
          key={orderMethod}
        >
          <Radio
            checked={cached_order.method === orderMethod}
            sx={{
              color: Theme.color.blue['300'],
              '&.Mui-checked': {
                color: Theme.color.blue['300'],
              },
            }}
            key={orderMethod}
          />
          <p className="ml-xs my-auto text-sm">{t(`sections.method.${orderMethod}.title`)}</p>
        </button>
      ))}
    </OrderInfo.Row>
  );
};

OrderInfo.Time = ({ onChange }: { onChange: (props?: Partial<OrderMethodModalProps>) => void }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:OrderInfo' });
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { cached_order } = useStore<REDUX_STORE_SLICES.ORDER>(REDUX_STORE_SLICES.ORDER);
  const { isOrderMethodCurrentlyOpen, isOrderMethodOpenForFutureOrders } = useOrderMethodsStatus(company);
  const { scheduled_for: scheduledFor, method: orderMethod } = cached_order;
  const hasAsapOrders = company?.has_asap_orders && isOrderMethodCurrentlyOpen(orderMethod);
  const selectedTimeOption = scheduledFor ? ORDER_TIME_OPTIONS.scheduled : ORDER_TIME_OPTIONS.asap;
  const hasScheduledOrders = company?.has_scheduled_orders || isOrderMethodOpenForFutureOrders(orderMethod);

  const timeOptions = useMemo<OrderTimeOption[]>(
    () =>
      collect()
        .when(hasAsapOrders, (data) =>
          data.push({
            id: ORDER_TIME_OPTIONS.asap,
            active: selectedTimeOption === ORDER_TIME_OPTIONS.asap,
            title: t(`sections.time.method.${ORDER_TIME_OPTIONS.asap}.title`, {
              time: HelperMethods.formatTimeInterval(
                orderMethod === ORDER_METHODS.DELIVERY
                  ? company?.average_order_delivery_time
                  : company?.average_order_preparation_time,
              ),
            }),
          }),
        )
        .when(hasScheduledOrders, (data) =>
          data.push({
            id: ORDER_TIME_OPTIONS.scheduled,
            active: selectedTimeOption === ORDER_TIME_OPTIONS.scheduled,
            title:
              company?.scheduled_order_option_title ?? t(`sections.time.method.${ORDER_TIME_OPTIONS.scheduled}.title`),
            description: scheduledFor ? moment(scheduledFor).format('ddd HH:mm, MMM DD') : null,
          }),
        )
        .toArray(),
    [hasAsapOrders, hasScheduledOrders, company, selectedTimeOption, scheduledFor, orderMethod],
  );
  return (
    <OrderInfo.Row title={t('sections.time.title')}>
      {timeOptions.map((timeOption) => (
        <button
          className="flex flex-row w-fit items-center mt-xs"
          onClick={() => onChange({ openModal: true, orderMethod: cached_order.method })}
          key={timeOption.id}
        >
          <Radio
            checked={selectedTimeOption === timeOption.id}
            sx={{
              color: Theme.color.blue['300'],
              '&.Mui-checked': {
                color: Theme.color.blue['300'],
              },
            }}
            key={timeOption.id}
          />
          <div className="flex flex-col ml-xs text-left">
            <p className="my-auto text-sm">{timeOption.title}</p>
            {timeOption.description && <p className="my-auto text-sm">{timeOption.description}</p>}
          </div>
        </button>
      ))}
    </OrderInfo.Row>
  );
};

OrderInfo.Location = ({ onChange }: { onChange: (props?: Partial<OrderMethodModalProps>) => void }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:OrderInfo' });
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { cached_order } = useStore<REDUX_STORE_SLICES.ORDER>(REDUX_STORE_SLICES.ORDER);

  const locationDetails = useMemo(
    () =>
      ({
        [ORDER_METHODS.PICKUP]: {
          title: 'Store Address',
          description: `${company?.name}, ${AddressHelpers.setCountry(company?.country).format({
            street_name: company?.address,
            city: company?.city,
            zip_code: company?.zip_code,
          })}`,
        },
        [ORDER_METHODS.DELIVERY]: {
          description: AddressHelpers.setCountry(company?.country).format({
            street_name: cached_order.delivery_street_name,
            street_number: cached_order.delivery_street_number,
            city: cached_order.delivery_city,
            zip_code: cached_order.delivery_zip_code,
          }),
        },
        [ORDER_METHODS.ROOM_SERVICE]: {
          description: collect([
            cached_order.room_service_location?.name,
            AddressHelpers.setCountry(company?.country).format({
              street_name: cached_order.room_service_location?.street_name,
              street_number: cached_order.room_service_location?.street_number,
            }),
            cached_order.room_service_details
              ? `${t(`${cached_order.room_service_location?.type}.details_label`)}: ${cached_order.room_service_details}`
              : null,
          ])
            .whereNotNull()
            .implode(', '),
        },
      })[cached_order.method],
    [
      company,
      company,
      cached_order.method,
      cached_order.room_service_location,
      cached_order.room_service_details,
      cached_order.delivery_street_name,
      cached_order.delivery_street_number,
    ],
  );

  return (
    <OrderInfo.Row
      title={t(`sections.location.${cached_order.method ?? ORDER_METHODS.PICKUP}.title`)}
      onChange={
        cached_order.method === ORDER_METHODS.PICKUP
          ? undefined
          : () => onChange({ openModal: true, orderMethod: cached_order.method })
      }
    >
      {locationDetails.title && <p className="text-sm">{locationDetails.title}</p>}
      <p className="text-sm">{locationDetails.description}</p>
    </OrderInfo.Row>
  );
};

OrderInfo.Row = ({ title, children, onChange }: SectionProps): ReactElement => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:OrderInfo' });
  return (
    <div className={classnames('p-md border-xs rounded-lg bg-brand-card border-mobile-secondary space-y-md')}>
      <div className="flex justify-between font-medium text-xs">
        <p className="text-xs text-brand-text-secondary font-medium">{title}</p>
        {onChange && (
          <button onClick={onChange} className="text-brand-primary">
            {t('buttons.change')}
          </button>
        )}
      </div>
      <div className="[&_*]:text-sm [&_*]:font-medium">{children}</div>
    </div>
  );
};
